// Environment types
export type Environment = 'local' | 'develop' | 'sit' | 'preprod' | 'production'

export type LogLevel = 'debug' | 'info' | 'warn' | 'error'

// Environment-specific configuration
export interface EnvironmentConfig {
  enabled: boolean
  logLevel: LogLevel
  logToConsole: boolean
  sentry: {
    enabled: boolean
    tracesSampleRate: number
    replaysSessionSampleRate: number
    replaysOnErrorSampleRate: number
    sendEvents: boolean
  }
}

// Centralized environment configuration
export const ENV_CONFIG: Record<Environment, EnvironmentConfig> = {
  local: {
    enabled: true,
    logLevel: 'debug',
    logToConsole: true,
    sentry: {
      enabled: false,
      tracesSampleRate: 1.0,
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0,
      sendEvents: false,
    },
  },
  develop: {
    enabled: true,
    logLevel: 'debug',
    logToConsole: true,
    sentry: {
      enabled: true,
      tracesSampleRate: 1.0,
      replaysSessionSampleRate: 0.5,
      replaysOnErrorSampleRate: 1.0,
      sendEvents: true,
    },
  },
  sit: {
    enabled: true,
    logLevel: 'info',
    logToConsole: false,
    sentry: {
      enabled: true,
      tracesSampleRate: 0.5,
      replaysSessionSampleRate: 0.3,
      replaysOnErrorSampleRate: 1.0,
      sendEvents: true,
    },
  },
  preprod: {
    enabled: true,
    logLevel: 'warn',
    logToConsole: false,
    sentry: {
      enabled: true,
      tracesSampleRate: 0.3,
      replaysSessionSampleRate: 0.2,
      replaysOnErrorSampleRate: 1.0,
      sendEvents: true,
    },
  },
  production: {
    enabled: true,
    logLevel: 'error',
    logToConsole: false,
    sentry: {
      enabled: true,
      tracesSampleRate: 0.1,
      replaysSessionSampleRate: 0.05,
      replaysOnErrorSampleRate: 1.0,
      sendEvents: true,
    },
  },
}

// Get current environment from environment variables
export function getCurrentEnvironment(): Environment {
  return import.meta.env.VITE_APP_ENV || 'local'
}

// Get current environment config
export function getEnvironmentConfig(): EnvironmentConfig {
  const env = getCurrentEnvironment()
  return ENV_CONFIG[env] || ENV_CONFIG.local
}

const DEFAULT_MAPBOX_TOOL_URL = 'https://geojson.io'

// Get current environment tool mapbox url
export function getMapboxUrl(): string {
  return import.meta.env.VITE_MAPBOX_TOOL || DEFAULT_MAPBOX_TOOL_URL
}

// Environment checks
export function isProduction(): boolean {
  const env = getCurrentEnvironment()
  return env === 'production'
}

export function isDevelopment(): boolean {
  const env = getCurrentEnvironment()
  return env === 'local' || env === 'develop'
}

export function isSentryEnabled(): boolean {
  const config = getEnvironmentConfig()
  return config.sentry.enabled
}
